import React, { useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Pagination from '@mui/material/Pagination';
import Checkbox from '@mui/material/Checkbox';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { BillingReleaseRecordInterface } from '../../interfaces/BillingRelease';
import { StyledTableSortLabel } from '../../styles/kanriStyle';

interface Order {
  target_ym: 'asc' | 'desc',
  close_date: 'asc' | 'desc',
  user_id: 'asc' | 'desc',
  contract_id: 'asc' | 'desc',
  contract_name: 'asc' | 'desc',
  document_id: 'asc' | 'desc',
}

type OrderBy = keyof Order;

interface BillingReleaseTableProps {
  searchedData: BillingReleaseRecordInterface[];
  page: number;
  pagenation: (page: number) => void;
  allCheck: (state: boolean) => void;
  singleCheck: (documentId: string) => void;
}

const itemsPerPage: number = 20;

const pagenationTheme = createTheme({
  components: {
    MuiPagination: {
      styleOverrides: {
        root: ({theme}) =>
          theme.unstable_sx({
            display: "flex",
            justifyContent: "center"
          }),
        },
      },
    },
  },
);

export default function BillingReleaseTable(props: BillingReleaseTableProps) {
  const { searchedData, page, pagenation, allCheck, singleCheck } = props;
  const [checkboxBulk, setCheckboxBulk] = React.useState<boolean>(false);
  const [order, setOrder] = useState<Order>({
    target_ym: 'asc',
    close_date: 'asc',
    user_id: 'asc',
    contract_id: 'asc',
    contract_name: 'asc',
    document_id: 'asc',
  });
  const [orderBy, setOrderBy] = React.useState<OrderBy>('contract_id');
  
  const handleRequestSort = (property: OrderBy) => {
    const isAsc = order[property] === 'asc';
    setOrderBy(property);
    setOrder((prevOrder) => ({
      ...prevOrder,
      [property]: isAsc ? 'desc' : 'asc',
    }));
  };
  
  const handleChangeCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckboxBulk(event.target.checked);
    allCheck(event.target.checked);
  };
  
  // カラムごとによる行の並び替え
  const compare = (a: BillingReleaseRecordInterface, b: BillingReleaseRecordInterface): number => {
    if (orderBy === 'target_ym') {
      if (a.target_ym < b.target_ym) return order.target_ym === 'asc' ? -1 : 1;
      if (a.target_ym > b.target_ym) return order.target_ym === 'asc' ? 1 : -1;
    } else if (orderBy === 'close_date') {
      if (a.close_date < b.close_date) return order.close_date === 'asc' ? -1 : 1;
      if (a.close_date > b.close_date) return order.close_date === 'asc' ? 1 : -1;
    } else if (orderBy === 'user_id') {
      if (a.user_id < b.user_id) return order.user_id === 'asc' ? -1 : 1;
      if (a.user_id > b.user_id) return order.user_id === 'asc' ? 1 : -1;
    } else if (orderBy === 'contract_id') {
      if (a.contract_id < b.contract_id) return order.contract_id === 'asc' ? -1 : 1;
      if (a.contract_id > b.contract_id) return order.contract_id === 'asc' ? 1 : -1;
    } else if (orderBy === 'contract_name') {
      if (a.contract_name < b.contract_name) return order.contract_name === 'asc' ? -1 : 1;
      if (a.contract_name > b.contract_name) return order.contract_name === 'asc' ? 1 : -1;
    } else if (orderBy === 'document_id') {
      if (a.document_id < b.document_id) return order.document_id === 'asc' ? -1 : 1;
      if (a.document_id > b.document_id) return order.document_id === 'asc' ? 1 : -1;
    }
    return 0;
  };
    
  return (
    <div>
      <Typography variant="h6">{`${(page-1)*itemsPerPage+1}-${(page)*itemsPerPage < searchedData.length? (page)*itemsPerPage : searchedData.length} / ${searchedData.length}件`}</Typography>
      <TableContainer >
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>
                <Checkbox checked={checkboxBulk} onChange={handleChangeCheckbox} />
              </TableCell>
              <TableCell>
                <StyledTableSortLabel
                  active={orderBy === 'target_ym'}
                  direction={order.target_ym}
                  onClick={() => handleRequestSort('target_ym')}>対象年月</StyledTableSortLabel>
              </TableCell>
              <TableCell>
                <StyledTableSortLabel
                  active={orderBy === 'close_date'}
                  direction={order.close_date}
                  onClick={() => handleRequestSort('close_date')}>発行期限</StyledTableSortLabel>
              </TableCell>
              <TableCell>
                <StyledTableSortLabel
                  active={orderBy === 'user_id'}
                  direction={order.user_id}
                  onClick={() => handleRequestSort('user_id')}>ユーザID</StyledTableSortLabel>
              </TableCell>
              <TableCell>
                <StyledTableSortLabel
                  active={orderBy === 'contract_id'}
                  direction={order.contract_id}
                  onClick={() => handleRequestSort('contract_id')}>請求先ID</StyledTableSortLabel>
              </TableCell>
              <TableCell>
                <StyledTableSortLabel
                  active={orderBy === 'contract_name'}
                  direction={order.contract_name}
                  onClick={() => handleRequestSort('contract_name')}>契約名</StyledTableSortLabel>
              </TableCell>
              <TableCell>
                <StyledTableSortLabel
                  active={orderBy === 'document_id'}
                  direction={order.document_id}
                  onClick={() => handleRequestSort('document_id')}>請求書番号</StyledTableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {searchedData.sort(compare).slice((page - 1) * itemsPerPage, (page - 1) * itemsPerPage + itemsPerPage).map((row, index) => (
              <TableRow
                sx={{ '&:last-child td, &:last-child th': { border: 0 },
                backgroundColor: index % 2 === 1 ? '#D9E1FB' : '#FFFFFF' }}
              >
                <TableCell>
                  <Checkbox
                    onChange={() => singleCheck(row.document_id)}
                    checked={row.checked} />
                </TableCell>
                <TableCell>{row.target_ym}</TableCell>
                <TableCell>{row.close_date}</TableCell>
                <TableCell>{row.user_id}</TableCell>
                <TableCell>{row.contract_id}</TableCell>
                <TableCell>{row.contract_name}</TableCell>
                <TableCell>{row.document_id}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <>
        {
          (searchedData.length === 0) ? <p style={{textAlign: 'center'}}>データがありません</p> : <></>
        }
      </>
      <ThemeProvider theme={pagenationTheme}>
        <Pagination 
          count={(searchedData.length % itemsPerPage === 0) ? (Math.floor((searchedData.length / itemsPerPage) + 1)-1) : (Math.floor((searchedData.length / itemsPerPage) + 1))}
          variant="outlined" 
          shape="rounded" 
          onChange={(e, page) => {
              pagenation(page);
            }
          } 
          page={page}
        />
      </ThemeProvider>
    </div>
  );
}